import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCartSnapshot } from "../hooks/useCart";
import { addToCart } from "../services/cartService";
import { FALLBACK_PRODUCT_IMAGE, fetchProducts } from "../services/commerceApi";
import { routePaths } from "../constants/routes";

export default function ProductSearchListing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { cartIds, syncCartSnapshot } = useCartSnapshot();

  useEffect(() => {
    let isMounted = true;

    async function loadProducts() {
      setLoading(true);
      try {
        const response = await fetchProducts({ limit: 200 });
        if (isMounted) {
          setProducts(response);
        }
      } catch (error) {
        console.error("Unable to load search listing", error);
        if (isMounted) {
          setProducts([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return products;
    }

    return products.filter((product) =>
      [product.name, product.brand, product.category, product.description]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [products, query]);

  const handleSearch = (event) => {
    event.preventDefault();
    navigate(`${routePaths.productSearch}?q=${encodeURIComponent(query.trim())}`);
  };

  const handleAddToCart = (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    if (cartIds.has(String(product.id))) {
      navigate(routePaths.cart);
      return;
    }
    addToCart(product);
    syncCartSnapshot();
    toast.success(`${product.name} added to cart.`);
  };

  return (
    <main className="mobile-tablets-page">
      <nav className="breadcrumb-nav">
        <NavLink to={routePaths.home} className="breadcrumb-item">Home</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <NavLink to={routePaths.product} className="breadcrumb-item">Catalog</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <span className="breadcrumb-item active-page">Search Results</span>
      </nav>

      <section className="black-friday-banner">
        <div className="banner-content">
          <div className="banner-left">
            <div className="jumia-logo">QA Coder <span className="star-icon">*</span></div>
            <div className="black-friday-text">Product Search</div>
            <div className="date-range">Search listing page for ecommerce automation flows</div>
          </div>
          <div className="banner-right">
            <h2>Search, refine, and validate listing behavior</h2>
            <p>Use this page for keyword search, result assertions, cart actions, and product detail navigation.</p>
          </div>
        </div>
      </section>

      <section
        style={{
          maxWidth: "1200px",
          margin: "0 auto 18px",
          padding: "18px 20px",
          borderRadius: "14px",
          background: "#fff",
          boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
        }}
      >
        <form
          onSubmit={handleSearch}
          style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "12px" }}
        >
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search product, brand, or category"
            style={{
              minHeight: "46px",
              padding: "12px 14px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
            }}
          />
          <button
            type="submit"
            style={{
              minHeight: "46px",
              padding: "12px 18px",
              borderRadius: "12px",
              border: "none",
              background: "#ea580c",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Search
          </button>
        </form>
        <p style={{ marginTop: "12px", color: "#64748b" }}>
          Showing {visibleProducts.length} results for <strong>{query.trim() || "all products"}</strong>
        </p>
      </section>

      <section className="product-grid">
        {loading ? <p>Loading search results...</p> : null}
        {!loading && visibleProducts.length === 0 ? (
          <div className="empty-state-card" style={{ maxWidth: "900px", margin: "0 auto 32px" }}>
            No products found for this keyword. Try another product name or browse categories.
          </div>
        ) : null}

        {!loading &&
          visibleProducts.map((product) => (
            <NavLink to={routePaths.productDetails(product.id)} className="product-card" key={product.id}>
              <span className="discount-badge">-{product.discount}%</span>
              <div className="product-image-container">
                <img
                  src={product.listingImageUrl || product.url || FALLBACK_PRODUCT_IMAGE}
                  alt={product.name}
                  className="product-img"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="product-info">
                <div className="rating">
                  <i className="fas fa-star"></i> {product.rating} ({product.reviewCount})
                </div>
                <h3 className="product-name">{product.name}</h3>
                <p className="current-price">{product.currentPriceFormatted}</p>
                <p className="original-price">{product.originalPriceFormatted}</p>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  <button
                    type="button"
                    onClick={(event) => handleAddToCart(event, product)}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      borderRadius: "10px",
                      border: "none",
                      background: "#1d4ed8",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {cartIds.has(String(product.id)) ? "Go to Cart" : "Add to Cart"}
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate(routePaths.productDetails(product.id));
                    }}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      borderRadius: "10px",
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#0f172a",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            </NavLink>
          ))}
      </section>
    </main>
  );
}
